// @flow
import {
  Card,
  CardContent,
  Checkbox,
  CardHeader,
  Box,
  Typography,
  Stack,
  Skeleton,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { navigation } from 'services/navigation'
import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import intl from 'localization/components'
import { useCreditCheck } from 'pages/credit-check/context'
import { useFinancialStats } from 'services/queries'
import { take } from 'ramda'
import Capitalize from 'lodash/capitalize'
import PropertyWrapper from 'pages/credit-check/scenes/PropertyWrapper'

const styles = {
  checkbox: {
    padding: 0,
    cursor: 'default',
  },
}

const AuditorInfo = () => {
  const { localId } = useCreditCheck()
  const financialStatsQuery = useFinancialStats(localId)

  if (financialStatsQuery.isFetching) {
    const finStats = Array.from({ length: 4 }, (x, i) => i) // generate 4 cards to display
    const finStatsData = Array.from({ length: 8 }, (x, i) => i) // generates 8 rows inside the card
    const generateRandomWidth = () => {
      return `${Math.random() * (80 - 20) + 20}%` // generates a random width between 20% to 80%
    }
    return (
      <Stack direction="row" spacing={4}>
        {finStats.map((i) => (
          <Card style={{ flex: 1 }} sx={{ boxShadow: 2, padding: 2 }} key={i}>
            <Skeleton
              variant="rounded"
              width="80%"
              height={20}
              animation="wave"
              sx={{ marginBottom: '3rem', marginTop: '2rem' }}
            />
            <Stack spacing={4}>
              {finStatsData.map((x) => {
                return (
                  <Stack spacing={1} key={x}>
                    <Skeleton
                      variant="rectangular"
                      width={generateRandomWidth()}
                      height={15}
                      animation="wave"
                    />
                    <Skeleton
                      variant="rectangular"
                      width={generateRandomWidth()}
                      height={15}
                      animation="wave"
                    />
                  </Stack>
                )
              })}
            </Stack>
          </Card>
        ))}
      </Stack>
    )
  }

  const data = take(4, financialStatsQuery.data ?? [])
  return (
    <Stack direction="row" data-cy="auditor-info" spacing={4}>
      {data.map((x, i) => (
        <Card style={{ flex: 1 }} sx={{ boxShadow: 2, paddingBottom: 4 }} key={i}>
          <CardHeader
            disableTypography
            title={
              <Typography variant="subtitle1">
                {`${moment(x.period.start).format('DD.MM.YYYY')} -
                            ${moment(x.period.end).format('DD.MM.YYYY')}`}
              </Typography>
            }
          />
          <CardContent
            sx={{
              '&.MuiCardContent-root:last-child': { paddingBottom: 4 },
            }}
          >
            <Stack direction="column" spacing={2}>
              <PropertyWrapper titleIntlId={intl.annualReportInformation('approvalDate')}>
                {x.approval_date && moment(x.approval_date).format('DD.MM.YYYY')}
              </PropertyWrapper>

              <PropertyWrapper
                titleIntlId={intl.annualReportInformation('classOfReportingEntity')}
              >
                {x.class_of_reporting_entity}
              </PropertyWrapper>

              <PropertyWrapper titleIntlId={intl.annualReportInformation('ifrs')}>
                {
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <Typography variant="body2">
                        {x.ifrs ? (
                          <FormattedMessage id={intl.generic('yes')} />
                        ) : (
                          <FormattedMessage id={intl.generic('no')} />
                        )}
                      </Typography>
                    </Box>
                    <Checkbox
                      className={styles.checkbox}
                      indeterminate={!x.ifrs}
                      checked={x.ifrs}
                      value="checkedB"
                      style={x.ifrs ? { color: 'green' } : { color: 'red' }}
                    />
                  </Stack>
                }
              </PropertyWrapper>

              <PropertyWrapper titleIntlId={intl.annualReportInformation('name')}>
                {x.auditor.name}
              </PropertyWrapper>

              <PropertyWrapper titleIntlId={intl.annualReportInformation('description')}>
                {x.auditor.description}
              </PropertyWrapper>

              <PropertyWrapper
                titleIntlId={intl.annualReportInformation('type_of_assistance')}
              >
                {x.auditor.type_of_assistance}
              </PropertyWrapper>

              <PropertyWrapper titleIntlId={intl.annualReportInformation('company_name')}>
                {x.auditor.company_name && (
                  <Link to={navigation.creditCheck(x.auditor.local_organization_id)}>
                    <Typography variant="body2">
                      {Capitalize(x.auditor.company_name)}
                    </Typography>
                  </Link>
                )}
              </PropertyWrapper>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}

export default AuditorInfo
// TODO: change this to TS
